/**
 * Canonical web builder for `GET /api/profile/suggested-users` — the seller
 * discovery list rendered by the feed's right rail and the profile page's
 * follow carousel.
 *
 * Both surfaces used to `fetch` this endpoint inline from the component, each
 * with its own local copy of the row type, and the rail additionally guessed
 * the envelope (`Array.isArray(data) ? data : data.items ?? data.users ?? []`)
 * for a route that has only ever answered `{ items }`. Centralised per the
 * Frontend HARD GATE: `components/**` must not call `/api/*` inline, and a
 * shape that exists in `@kraahl/contract` must not be hand-declared beside it.
 *
 * Consumers go through `hooks/use-suggested-sellers.ts`, not this module
 * directly — the hook owns request lifetime (abort on unmount / prop change).
 *
 * @see packages/contract/src/profile-suggested-users.ts — schema (SoT)
 * @see app/api/profile/suggested-users/route.ts — server emitter
 * @see apps/mobile/lib/api-profile-suggestions.ts — sibling adapter, same endpoint
 * @see .tondocs/skills/hex.md §2 — the component owns the click, lib owns the call
 */

import {
  parseApiResponse,
  ProfileSuggestedUsersResponseSchema,
  type ProfileSuggestedUser,
  type ProfileSuggestedUsersResponse,
} from "@kraahl/contract";

export interface FetchSuggestedSellersArgs {
  /** Profile to leave out — the viewed seller on a profile page, the viewer on the feed. */
  excludeProfileId?: string | null;
  /** Route clamps to 1..20; omit to take its default of 12. */
  limit?: number;
  /** Threaded into `fetch` so the caller can cancel on unmount. */
  signal?: AbortSignal;
}

/**
 * Drop rows the schema could not parse.
 *
 * A single malformed seller must not blank an entire discovery surface — the
 * rail and the carousel are both decorative next to the feed itself — so the
 * response schema marks rows `.nullable().catch(null)` and the reduction to
 * "only the rows that parsed" happens here, in one place, rather than in each
 * consumer's render.
 */
export function compactSuggestedSellers(
  body: ProfileSuggestedUsersResponse,
): ProfileSuggestedUser[] {
  return body.items.filter((row): row is ProfileSuggestedUser => row !== null);
}

/**
 * Fetch the suggested-seller list, boundary-parsed.
 *
 * Throws on non-2xx and on envelope mismatch (`ApiResponseParseError`) — it
 * does NOT swallow. The hook above decides that a failed discovery rail simply
 * renders nothing.
 */
export async function fetchSuggestedSellers(
  args: FetchSuggestedSellersArgs = {},
): Promise<ProfileSuggestedUser[]> {
  const params = new URLSearchParams();
  if (args.limit != null) params.set("limit", String(args.limit));
  if (args.excludeProfileId) params.set("excludeProfileId", args.excludeProfileId);

  const query = params.toString();
  const res = await fetch(
    `/api/profile/suggested-users${query ? `?${query}` : ""}`,
    { ...(args.signal ? { signal: args.signal } : {}) },
  );

  if (!res.ok) {
    throw new Error(`Suggested-sellers API response not ok: ${res.status}`);
  }

  return compactSuggestedSellers(
    await parseApiResponse(res, ProfileSuggestedUsersResponseSchema),
  );
}
